import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Users, Mail, Phone, Building2, Search, Reply, ChevronRight, Loader2, X,
         Archive, Trash2, AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  contact: "Contact",
  quote: "Quote",
  assessment: "Assessment",
  privacy_check: "Privacy",
  campaign_application: "Campaign",
  code_update_request: "Code update",
};

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    try {
      const r = await api.get(`/admin/customers${q}`);
      setItems(r.data || []);
    } catch (_e) {
      /* ignore */
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (email) => {
    setSelectedEmail(email);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const r = await api.get(`/admin/customers/${encodeURIComponent(email)}`);
      setDetail(r.data);
    } catch (_e) {
      /* ignore */
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedEmail(null);
    setDetail(null);
  };

  const doAction = async (kind) => {
    const label = { archive: "archive", erase: "erase (GDPR Art. 17)" }[kind];
    if (!window.confirm(`Confirm ${label} for ${selectedEmail}?`)) return;
    try {
      await api.post(`/admin/customers/${encodeURIComponent(selectedEmail)}/${kind}`, { confirm: true });
      toast.success(`${kind === "archive" ? "Archived" : "Erased"} ${selectedEmail}`);
      closeDetail();
      load();
    } catch (e) { toast.error("Failed — see console"); console.error(e); }
  };

  const doPurgeDemo = async () => {
    const phrase = window.prompt('Type "DELETE DEMO DATA" (all-caps) to remove all submissions with example.com / resend.dev / test.local / TEST_ prefixes.');
    if (phrase !== "DELETE DEMO DATA") return;
    try {
      const r = await api.post("/admin/customers/purge-demo", { confirm_phrase: phrase });
      toast.success(`Deleted ${r.data.submissions_deleted} demo submissions`);
      load();
    } catch (e) { toast.error("Failed — see console"); console.error(e); }
  };

  return (
    <div data-testid="admin-customers">
      <span className="eyebrow">Directory</span>
      <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
        <Users size={26} className="text-gold" />
        Customers
      </h1>
      <span className="divider-gold" />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative grow max-w-md">
          <Search size={16} className="absolute top-3.5 left-3 text-navy/40" />
          <input
            data-testid="customers-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or company…"
            className="field-box pl-10"
          />
        </div>
        <button
          data-testid="customers-purge-demo"
          onClick={doPurgeDemo}
          className="inline-flex items-center gap-2 border-2 border-navy/30 text-navy/70 hover:text-navy hover:border-gold px-3 py-2 text-xs rounded"
          title="Remove test/demo submissions"
        >
          <Wand2 size={14} className="text-gold" /> Clean demo data
        </button>
      </div>

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream">
            <tr>
              <th className="text-start p-3">Contact</th>
              <th className="text-start p-3">Company</th>
              <th className="text-start p-3">Submissions</th>
              <th className="text-start p-3">Types</th>
              <th className="text-start p-3">Last activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.email}
                data-testid={`customer-row-${c.email}`}
                className="border-t border-navy/10 hover:bg-cream cursor-pointer"
                onClick={() => openDetail(c.email)}
              >
                <td className="p-3">
                  <div className="text-navy font-medium">{c.name || <em className="text-navy/40">Unknown</em>}</div>
                  <div className="text-navy/70 text-xs flex items-center gap-1 mt-0.5">
                    <Mail size={11} className="text-gold" /> {c.email}
                  </div>
                  {c.phone && (
                    <div className="text-navy/70 text-xs flex items-center gap-1">
                      <Phone size={11} className="text-gold" /> {c.phone}
                    </div>
                  )}
                </td>
                <td className="p-3 text-navy/80">{c.company || "—"}</td>
                <td className="p-3">
                  <Badge className="bg-gold text-navy">{c.submissions_count}</Badge>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(c.types).map(([k, v]) => (
                      <Badge key={k} variant="secondary" className="text-[10px]">
                        {TYPE_LABEL[k] || k} · {v}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="p-3 text-navy/80 text-xs whitespace-nowrap">
                  {c.last_activity ? new Date(c.last_activity).toLocaleString() : "—"}
                </td>
                <td className="p-3 text-end text-navy/40">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-navy/50">
                No customers yet. Every quote, assessment or contact submission will appear here.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedEmail && (
        <div
          className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeDetail}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-md border border-gold/30 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="customer-detail"
          >
            <div className="p-8 border-b border-navy/10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="eyebrow">Customer profile</div>
                  {loadingDetail || !detail ? (
                    <div className="mt-2 text-navy/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                  ) : (
                    <>
                      <h2 className="mt-1 text-2xl text-navy">{detail.name || "Unnamed"}</h2>
                      <div className="text-navy/70 text-sm mt-1 flex flex-wrap gap-3 items-center">
                        <span className="flex items-center gap-1"><Mail size={12} className="text-gold" /> <a href={`mailto:${detail.email}`} className="hover:text-gold">{detail.email}</a></span>
                        {detail.phone && <span className="flex items-center gap-1"><Phone size={12} className="text-gold" /> {detail.phone}</span>}
                        {detail.company && <span className="flex items-center gap-1"><Building2 size={12} className="text-gold" /> {detail.company}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-xs text-navy/60">
                        <Badge className="bg-gold text-navy">{detail.submissions_count} submissions</Badge>
                        <span>First seen: {new Date(detail.first_seen).toLocaleDateString()}</span>
                        <span>Last activity: {new Date(detail.last_activity).toLocaleDateString()}</span>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={closeDetail} className="text-navy/50 hover:text-navy" data-testid="customer-close">
                  <X size={20} />
                </button>
              </div>
              {detail && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    data-testid="customer-archive"
                    onClick={() => doAction("archive")}
                    className="btn-outline-navy text-xs"
                    title="Deactivate — data kept, submissions archived"
                  >
                    <Archive size={13} /> Archive
                  </button>
                  <button
                    data-testid="customer-erase"
                    onClick={() => doAction("erase")}
                    className="inline-flex items-center gap-2 border-2 border-red-500/50 text-red-600 hover:bg-red-500 hover:text-white px-3 py-1.5 text-xs rounded"
                    title="GDPR Article 17 — permanently anonymise"
                  >
                    <Trash2 size={13} /> Erase (Art. 17)
                  </button>
                </div>
              )}
            </div>

            {detail && (
              <div className="p-8">
                <div className="text-xs uppercase tracking-widest text-navy/60 mb-3">Timeline</div>
                <div className="space-y-4">
                  {detail.thread.map((entry, idx) => (
                    <div key={entry.submission.id} className="relative pl-6 border-l-2 border-gold/40" data-testid={`timeline-${idx}`}>
                      <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-gold" />
                      <div className="bg-cream/50 p-4 rounded border border-navy/10">
                        <div className="flex items-center justify-between text-xs">
                          <Badge variant="secondary">{TYPE_LABEL[entry.submission.type] || entry.submission.type}</Badge>
                          <div className="text-navy/60">{new Date(entry.submission.created_at).toLocaleString()}</div>
                        </div>
                        {entry.submission.subject && (
                          <div className="mt-2 font-medium text-navy">{entry.submission.subject}</div>
                        )}
                        {entry.submission.message && (
                          <div className="mt-1 text-sm text-navy/80 whitespace-pre-wrap line-clamp-4">
                            {entry.submission.message}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <Badge className={entry.submission.status === "new" ? "bg-gold text-navy" : "bg-navy/10 text-navy"}>
                            {entry.submission.status}
                          </Badge>
                          {entry.submission.score != null && (
                            <span className="text-navy/60">Score: {Math.round(entry.submission.score)}</span>
                          )}
                        </div>
                        {entry.replies.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-navy/10">
                            <div className="text-xs uppercase tracking-widest text-navy/50 mb-2 flex items-center gap-1">
                              <Reply size={11} /> {entry.replies.length} repl{entry.replies.length === 1 ? "y" : "ies"}
                            </div>
                            {entry.replies.map((r) => (
                              <div key={r.id} className="text-xs text-navy/70 py-1">
                                <span className="font-semibold">{r.sent_by}</span>: {r.subject}
                                <span className="text-navy/50 ml-2">· {new Date(r.created_at).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
